import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import logo from "@/assets/connxt.png";
import logo_desktop from "@/assets/fynxt_blue.png";
import { useToast } from "@/components/common/Toast";
import { useAuthStore } from "@/store/auth.store";

const ResetPassword = () => {
	const { success, error: showError } = useToast();
	const navigate = useNavigate();
	const resetAuth = useAuthStore((state) => state.resetAuth);
	const { token } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState({
		password: "",
		confirmPassword: "",
		general: ""
	});
	const [formData, setFormData] = useState({
		password: "",
		confirmPassword: ""
	});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));

		setErrors((prev) => ({
			...prev,
			[name]: "",
			general: ""
		}));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setErrors({
			password: "",
			confirmPassword: "",
			general: ""
		});

		if (!formData.password) {
			setErrors((prev) => ({
				...prev,
				password: "Password is required"
			}));
			return;
		}

		if (formData.password.length < 8) {
			setErrors((prev) => ({
				...prev,
				password: "Password must be at least 8 characters"
			}));
			return;
		}

		if (formData.password !== formData.confirmPassword) {
			setErrors((prev) => ({
				...prev,
				confirmPassword: "Passwords do not match"
			}));
			return;
		}

		try {
			setIsLoading(true);
			await publicApiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["USER", "RESET_PASSWORD"], token),
				{ password: formData.password }
			);
			success("Password reset successfully");
			resetAuth();
			navigate("/login");
		} catch (err) {
			console.error("Error resetting password:", err);
			showError("Failed to reset password. Please try again.");
			setErrors((prev) => ({
				...prev,
				general: "Failed to reset password. Please try again."
			}));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col md:flex-row max-w-full w-full">
			<div className="w-full md:w-1/1 bg-[#002666]">
				<div className="flex items-center justify-center h-full">
					<img
						src={logo}
						alt="Logo"
						className="object-cover h-full max-w-full"
					/>
				</div>
			</div>

			<div className="w-full md:w-1/3 bg-white">
				<div className="flex min-h-full flex-col px-6 py-12 lg:px-8">
					<div className="sm:mx-auto sm:w-full sm:max-w-sm mt-24">
						<img
							src={logo_desktop}
							alt="Logo"
							className="object-cover h-full max-w-full sm:mx-auto"
						/>
						<h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 mt-2">
							Reset Password
						</h2>
					</div>

					<div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
						<form className="space-y-6" onSubmit={handleSubmit} noValidate>
							<div>
								<label
									htmlFor="password"
									className="block text-sm font-medium text-gray-700 text-left mb-1"
								>
									New Password
								</label>
								<input
									name="password"
									type="password"
									required
									aria-invalid={!!errors.password}
									aria-describedby={
										errors.password ? "password-error" : undefined
									}
									value={formData.password}
									onChange={handleChange}
									className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
										errors.password ? "ring-red-500" : "ring-gray-300"
									} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
									placeholder="Enter your new password"
									disabled={isLoading}
								/>
								{errors.password && (
									<p
										className="mt-1 text-sm text-red-600 text-left"
										role="alert"
									>
										{errors.password}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor="confirmPassword"
									className="block text-sm font-medium text-gray-700 text-left mb-1"
								>
									Confirm Password
								</label>
								<input
									name="confirmPassword"
									type="password"
									required
									aria-invalid={!!errors.confirmPassword}
									aria-describedby={
										errors.confirmPassword
											? "confirm-password-error"
											: undefined
									}
									value={formData.confirmPassword}
									onChange={handleChange}
									className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
										errors.confirmPassword ? "ring-red-500" : "ring-gray-300"
									} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
									placeholder="Confirm your new password"
									disabled={isLoading}
								/>
								{errors.confirmPassword && (
									<p className="mt-1 text-sm text-red-600" role="alert">
										{errors.confirmPassword}
									</p>
								)}
							</div>

							<div>
								<button
									type="submit"
									className="flex w-full justify-center rounded-md bg-[#002666] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={isLoading}
									aria-busy={isLoading}
								>
									{isLoading ? (
										<output className="flex items-center">
											<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
											<span>Resetting...</span>
										</output>
									) : (
										"Reset Password"
									)}
								</button>
							</div>

							<div className="mt-6">
								<div className="relative mb-4">
									<div className="absolute inset-0 flex items-center">
										<div className="w-full border-t border-gray-300" />
									</div>
									<div className="relative flex justify-center text-sm">
										<span className="bg-white px-2 text-gray-500">or</span>
									</div>
								</div>
								<button
									type="button"
									onClick={() => navigate("/login")}
									className="w-full text-center text-sm font-semibold text-indigo-600 hover:text-indigo-500 rounded-md"
								>
									Back to Sign In
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ResetPassword;
